import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import { useEffect, useRef, useState } from 'react'
import { useStore } from 'easy-peasy'
import PageMetaData from '@v4/utils/data/PageMetaData'
import AppStore from '@v4/store/appStore'
import useMountedRef from '@v4/hooks/useMountedRef'
import ThemeSelector from '@v4/utils/selectors/ThemeSelector'
import useWordings from '@v4/hooks/useWordings'

const useGeneralMetaData = ({ sectionId, sectionKey, overrideMetaData } = {}) => {
	const store = useStore()
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const sectionMetaRef = useRef(null)
	const scorecardRef = useRef(null)
	const themeDataRef = useRef(null)
	const themeSectionRef = useRef(null)
	const themeGalleryRef = useRef(null)
	const pageType = PageBuilderSelector.useType()
	const gallery = PageBuilderSelector.useGallery()
	const mountedRef = useMountedRef()
	const generalGallery =
		gallery &&
		((!gallery.sectionId && !sectionId) || gallery.sectionId === sectionId) &&
		!gallery.categoryId &&
		!gallery.index
			? gallery
			: null
	const { getWording } = useWordings()

	const [metaData, setMetaData] = useState(() => {
		const state = store.getState()
		const pageBuilderSelector = PageBuilderSelector.fromStoreState(state)
		const fields = pageBuilderSelector.selectTemplateFields(sectionKey)
		const groups = pageBuilderSelector.selectTemplateGroups(sectionKey)
		const template = pageBuilderSelector.selectSectionTemplate(sectionKey)
		const themeSectionKey = template?.theme_section
		const withSectionTheme = _.split(sectionKey, '/')[2] === 'theme'

		sectionMetaRef.current = pageBuilderSelector.selectMetaMap(sectionId)
		scorecardRef.current = ThemeSelector.selectExtendedScorecardData(state)
		themeDataRef.current = ThemeSelector.selectTheme(state)
		themeSectionRef.current = (withSectionTheme && ThemeSelector.selectSection(state, themeSectionKey)) || null
		themeGalleryRef.current = ThemeSelector.selectGallery(state)

		return _.reduce(
			fields,
			(acc, config, name) => {
				if (groups?.[config.group]?.repeatable) {
					return acc
				}
				const tierId = pageBuilderSelector.selectMetaItemActiveTierId(sectionId, { group: config.group })
				const themeConfig = ThemeSelector.selectSectionField(state, name, themeSectionKey)
				const meta = withSectionTheme
					? _.get(themeSectionRef.current, ['meta', name])
					: pageBuilderSelector.selectMeta(sectionId, { name, group: config.group, tierId })

				const sectionMetaData = new PageMetaData(
					meta,
					{
						...themeConfig,
						...config,
					},
					scorecardRef.current,
					{
						pageType,
						isBuilderMode,
						preview: generalGallery?.name === name ? generalGallery.preview : null,
						name,
						tierId,
						translate: getWording,
						themeData: themeDataRef.current,
						themeSectionKey: withSectionTheme ? themeSectionKey : undefined,
						themeGallery: themeGalleryRef.current,
					},
				)

				if (config?.withOverride && overrideMetaData?.[name]) {
					sectionMetaData.overriddenMetaData = overrideMetaData[name]
				}
				acc[name] = sectionMetaData
				return acc
			},
			{},
		)
	})

	const metaDataRef = useRef(metaData)

	useEffect(() => {
		const updateMetaData = () => {
			const newState = store.getState()
			const pageBuilderSelector = PageBuilderSelector.fromStoreState(newState)
			const newSectionMeta = pageBuilderSelector.selectMetaMap(sectionId)
			const newScorecard = ThemeSelector.selectExtendedScorecardData(newState)
			const newThemeData = ThemeSelector.selectTheme(newState)
			const template = pageBuilderSelector.selectSectionTemplate(sectionKey)
			const themeSectionKey = template?.theme_section
			const withSectionTheme = _.split(sectionKey, '/')[2] === 'theme'
			const newThemeSection = withSectionTheme ? ThemeSelector.selectSection(newState, themeSectionKey) : null
			const newThemeGallery = ThemeSelector.selectGallery(newState)

			if (
				sectionMetaRef.current === newSectionMeta &&
				scorecardRef.current === newScorecard &&
				themeDataRef.current === newThemeData &&
				themeSectionRef.current === newThemeSection &&
				themeGalleryRef.current === newThemeGallery
			) {
				return
			}
			sectionMetaRef.current = newSectionMeta
			scorecardRef.current = newScorecard
			themeDataRef.current = newThemeData
			themeSectionRef.current = newThemeSection
			themeGalleryRef.current = newThemeGallery

			const fields = pageBuilderSelector.selectTemplateFields(sectionKey)
			const groups = pageBuilderSelector.selectTemplateGroups(sectionKey)
			let updatesExist = false

			const newMetaData = _.reduce(
				fields,
				(acc, config, name) => {
					if (groups?.[config.group]?.repeatable) {
						return acc
					}
					const tierId = pageBuilderSelector.selectMetaItemActiveTierId(sectionId, { group: config.group })
					const themeConfig = ThemeSelector.selectSectionField(newState, name, themeSectionKey)
					const meta = withSectionTheme
						? _.get(newThemeSection, ['meta', name])
						: pageBuilderSelector.selectMeta(sectionId, { name, group: config.group, tierId })

					const pageMetaData = new PageMetaData(
						meta,
						{
							...themeConfig,
							...config,
						},
						scorecardRef.current,
						{
							pageType,
							isBuilderMode,
							preview: generalGallery?.name === name ? generalGallery.preview : null,
							name,
							tierId,
							translate: getWording,
							themeData: themeDataRef.current,
							themeSectionKey: withSectionTheme ? themeSectionKey : undefined,
							themeGallery: themeGalleryRef.current,
						},
					)

					if (config?.withOverride && overrideMetaData?.[name]) {
						pageMetaData.overriddenMetaData = overrideMetaData[name]
					}

					acc[name] = pageMetaData

					const existedMetaData = metaDataRef.current[name]

					if (
						!existedMetaData ||
						existedMetaData.data !== pageMetaData.data ||
						existedMetaData.tierKey !== pageMetaData.tierKey ||
						existedMetaData.scorecard !== pageMetaData.scorecard ||
						existedMetaData.themeData !== pageMetaData.themeData ||
						existedMetaData.overriddenMetaData !== pageMetaData.overriddenMetaData ||
						existedMetaData.preview !== pageMetaData.preview ||
						existedMetaData.defaultValue !== pageMetaData.defaultValue ||
						existedMetaData.themeSectionKey !== pageMetaData.themeSectionKey
					) {
						updatesExist = true
					} else {
						acc[name] = existedMetaData
					}

					return acc
				},
				{},
			)

			if (updatesExist && mountedRef.current) {
				metaDataRef.current = newMetaData
				setMetaData(newMetaData)
			}
		}

		updateMetaData()

		const unsubscribe = store.subscribe(updateMetaData)

		return () => {
			unsubscribe()
			sectionMetaRef.current = undefined
			scorecardRef.current = undefined
			themeDataRef.current = undefined
			themeSectionRef.current = undefined
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		sectionId,
		sectionKey,
		store,
		overrideMetaData,
		generalGallery,
		metaDataRef,
		mountedRef,
		isBuilderMode,
		getWording,
	])

	return metaData
}

export default useGeneralMetaData
